import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Save, RotateCcw, CheckCircle2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReadinessSettings() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const fetchCfg = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/readiness-config`, authHeaders());
      setCfg(res.data);
    } catch (e) {
      toast.error("Failed to load readiness config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCfg(); }, []);

  const setupSum = cfg ? cfg.setup_components.reduce((s, c) => s + (Number(c.points) || 0), 0) : 0;
  const trainSum = cfg ? cfg.training_components.reduce((s, c) => s + (Number(c.weight) || 0), 0) : 0;
  const blendSum = cfg ? (Number(cfg.blend.setup_weight) || 0) + (Number(cfg.blend.training_weight) || 0) : 0;

  const updateBlend = (key, val) => setCfg({ ...cfg, blend: { ...cfg.blend, [key]: Number(val) } });
  const updateSetup = (i, key, val) => {
    const arr = [...cfg.setup_components]; arr[i] = { ...arr[i], [key]: val }; setCfg({ ...cfg, setup_components: arr });
  };
  const updateTrain = (i, key, val) => {
    const arr = [...cfg.training_components]; arr[i] = { ...arr[i], [key]: val }; setCfg({ ...cfg, training_components: arr });
  };
  const updateGating = (key, val) => setCfg({ ...cfg, gating: { ...cfg.gating, [key]: val } });

  const save = async () => {
    if (blendSum !== 100) return toast.error(`Blend weights must total 100 (currently ${blendSum})`);
    if (setupSum !== 100) return toast.error(`Setup points must total 100 (currently ${setupSum})`);
    if (trainSum !== 100) return toast.error(`Training weights must total 100 (currently ${trainSum})`);
    try {
      setSaving(true);
      await axios.put(`${API}/admin/readiness-config`, cfg, authHeaders());
      toast.success("Readiness config saved — actor scores updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      const res = await axios.post(`${API}/admin/readiness-config/reset`, {}, authHeaders());
      setCfg(res.data.config);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error("Failed to reset");
    }
  };

  if (loading || !cfg) {
    return <div className="p-8 text-center text-slate-500">Loading readiness settings…</div>;
  }

  const SumBadge = ({ sum }) => (
    <Badge variant="outline" className={sum === 100 ? "bg-green-50 text-green-700 border-green-300" : "bg-red-50 text-red-700 border-red-300"}>
      {sum === 100 ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
      Total: {sum}/100
    </Badge>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="readiness-settings-page">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ai/actors')} data-testid="readiness-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">AI Readiness Score Settings</h1>
            <p className="text-sm text-muted-foreground">Define how each actor's readiness score (0–100) is calculated.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="readiness-reset-btn"><RotateCcw className="w-4 h-4 mr-2" />Reset</Button>
          <Button onClick={save} disabled={saving} data-testid="readiness-save-btn"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      {/* Blend */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">Overall Blend <SumBadge sum={blendSum} /></CardTitle>
          <CardDescription>How much Setup vs Training contributes to the final score.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label>Setup weight %</Label>
            <Input type="number" value={cfg.blend.setup_weight} onChange={(e) => updateBlend("setup_weight", e.target.value)} data-testid="blend-setup-weight" />
          </div>
          <div>
            <Label>Training weight %</Label>
            <Input type="number" value={cfg.blend.training_weight} onChange={(e) => updateBlend("training_weight", e.target.value)} data-testid="blend-training-weight" />
          </div>
        </CardContent>
      </Card>

      {/* Setup components */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">Setup Components <SumBadge sum={setupSum} /></CardTitle>
          <CardDescription>Configuration completeness. Points must total 100.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {cfg.setup_components.map((c, i) => (
            <div key={c.key} className="flex items-center gap-3" data-testid={`setup-row-${c.key}`}>
              <Switch checked={c.enabled} onCheckedChange={(v) => updateSetup(i, "enabled", v)} data-testid={`setup-enabled-${c.key}`} />
              <span className="flex-1 text-sm">{c.label}</span>
              <Input type="number" className="w-24" value={c.points} disabled={!c.enabled} onChange={(e) => updateSetup(i, "points", Number(e.target.value))} data-testid={`setup-points-${c.key}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Training components */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">Training Components <SumBadge sum={trainSum} /></CardTitle>
          <CardDescription>Real self-learning metrics. Weights must total 100.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {cfg.training_components.map((c, i) => (
            <div key={c.key} className="flex items-center gap-3" data-testid={`train-row-${c.key}`}>
              <Switch checked={c.enabled} onCheckedChange={(v) => updateTrain(i, "enabled", v)} data-testid={`train-enabled-${c.key}`} />
              <span className="flex-1 text-sm">{c.label}</span>
              {c.key === "quality" && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">conf N</span>
                  <Input type="number" className="w-16" value={c.confidence_sample_size} disabled={!c.enabled} onChange={(e) => updateTrain(i, "confidence_sample_size", Number(e.target.value))} data-testid="train-confidence-n" />
                </div>
              )}
              <Input type="number" className="w-24" value={c.weight} disabled={!c.enabled} onChange={(e) => updateTrain(i, "weight", Number(e.target.value))} data-testid={`train-weight-${c.key}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Gating */}
      <Card className="mb-6">
        <CardHeader><CardTitle>Gating Rules</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch checked={cfg.gating.require_kb_for_training} onCheckedChange={(v) => updateGating("require_kb_for_training", v)} data-testid="gating-require-kb" />
            <span className="text-sm flex-1">Require a knowledge base before training counts</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={cfg.gating.untrained_ceiling_enabled} onCheckedChange={(v) => updateGating("untrained_ceiling_enabled", v)} data-testid="gating-ceiling-enabled" />
            <span className="text-sm flex-1">Cap untrained actors at a maximum score</span>
            <Input type="number" className="w-24" value={cfg.gating.untrained_ceiling} disabled={!cfg.gating.untrained_ceiling_enabled} onChange={(e) => updateGating("untrained_ceiling", Number(e.target.value))} data-testid="gating-ceiling-value" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
